import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  SimpleGrid,
  Text,
  VStack,
  HStack,
  Center,
  Spinner,
  Icon,
  chakra,
  Field,
} from '@chakra-ui/react';
import {
  HiPlus,
  HiTrash,
  HiCalendarDays,
  HiUser,
  HiXCircle,
  HiCheckCircle,
} from 'react-icons/hi2';
import {
  Button,
  SelectInput,
  InputField,
  TextareaField,
  DialogComponent,
  StatusBadge,
} from '@themes/aegix';
import { useMyStudents } from '../hooks/useReunification';
import {
  guardianStudentName,
  type GuardianStudentWithLegacyIds,
} from '../services/reunification';
import {
  usePickupDelegations,
  useCreatePickupDelegation,
  useUpdatePickupDelegation,
  useDeletePickupDelegation,
} from '../hooks/useVisitorManagement';
import { toaster } from './ui/toaster';

// Helper to format ISO date/time
const formatDateTime = (isoString: string) => {
  try {
    const date = new Date(isoString);
    return date.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return isoString;
  }
};

// Helper to get local string for datetime-local value
const getLocalDatetimeString = (date: Date) => {
  const tzOffset = date.getTimezoneOffset() * 60000;
  const localISOTime = new Date(date.getTime() - tzOffset).toISOString();
  return localISOTime.slice(0, 16);
};

export function PickupDelegationView() {
  const { t } = useTranslation();
  const { data: studentsData, isLoading: loadingStudents } = useMyStudents();
  const { data: delegationsData, isLoading: loadingDelegations } = usePickupDelegations();

  const createMutation = useCreatePickupDelegation();
  const updateMutation = useUpdatePickupDelegation();
  const deleteMutation = useDeletePickupDelegation();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [delegateName, setDelegateName] = useState('');
  const [delegatePhone, setDelegatePhone] = useState('');
  const [delegateEmail, setDelegateEmail] = useState('');
  const [relationship, setRelationship] = useState('');
  const [validFrom, setValidFrom] = useState(() =>
    getLocalDatetimeString(new Date()),
  );
  const [validUntil, setValidUntil] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return getLocalDatetimeString(tomorrow);
  });
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The real guardian/students payload emits no `id`/`school_id`,
  // so this flow's invalid-data guard fires until a join key ships.
  const students: GuardianStudentWithLegacyIds[] = studentsData?.results ?? [];
  const delegations = delegationsData?.results ?? [];

  const handleOpen = () => {
    if (students.length > 0) {
      setSelectedStudentId(String(students[0].id));
    }
    setDelegateName('');
    setDelegatePhone('');
    setDelegateEmail('');
    setRelationship('');
    setValidFrom(getLocalDatetimeString(new Date()));
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setValidUntil(getLocalDatetimeString(tomorrow));
    setNotes('');
    setIsOpen(true);
  };

  const handleDelete = async (publicId: string) => {
    try {
      await deleteMutation.mutateAsync(publicId);
      toaster.create({
        title: t('delegation.deletedTitle'),
        description: t('delegation.deletedDesc'),
        type: 'success',
      });
    } catch {
      toaster.create({
        title: t('delegation.deleteErrorTitle'),
        description: t('delegation.deleteErrorDesc'),
        type: 'error',
      });
    }
  };

  const handleToggleActive = async (publicId: string, isActive: boolean) => {
    try {
      await updateMutation.mutateAsync({
        public_id: publicId,
        data: { is_active: !isActive },
      });
      toaster.create({
        title: isActive ? t('delegation.deactivatedTitle') : t('delegation.reactivatedTitle'),
        description: isActive
          ? t('delegation.deactivatedDesc')
          : t('delegation.reactivatedDesc'),
        type: 'success',
      });
    } catch {
      toaster.create({
        title: t('delegation.updateErrorTitle'),
        description: t('delegation.updateErrorDesc'),
        type: 'error',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudentId) {
      toaster.create({
        title: t('delegation.studentRequiredTitle'),
        description: t('delegation.studentRequiredDesc'),
        type: 'error',
      });
      return;
    }
    if (!delegateName.trim() || !relationship.trim()) {
      toaster.create({
        title: t('delegation.fieldsMissingTitle'),
        description: t('delegation.fieldsMissingDesc'),
        type: 'error',
      });
      return;
    }

    const student = students.find((s) => String(s.id) === selectedStudentId);
    if (!student || student.id === undefined) {
      toaster.create({
        title: t('delegation.invalidStudentTitle'),
        description: t('delegation.invalidStudentDesc'),
        type: 'error',
      });
      return;
    }

    if (new Date(validFrom) >= new Date(validUntil)) {
      toaster.create({
        title: t('delegation.invalidDateTitle'),
        description: t('delegation.invalidDateDesc'),
        type: 'error',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await createMutation.mutateAsync({
        student: student.id,
        delegate_name: delegateName,
        delegate_phone: delegatePhone,
        delegate_email: delegateEmail,
        relationship,
        valid_from: new Date(validFrom).toISOString(),
        valid_until: new Date(validUntil).toISOString(),
        notes,
      });

      toaster.create({
        title: t('delegation.addedTitle'),
        description: t('delegation.addedDesc', { name: delegateName }),
        type: 'success',
      });
      setIsOpen(false);
    } catch {
      toaster.create({
        title: t('delegation.createErrorTitle'),
        description: t('delegation.createErrorDesc'),
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const studentOptions = students.map((s) => ({
    label: guardianStudentName(s),
    value: String(s.id),
  }));

  const isLoading = loadingStudents || loadingDelegations;

  return (
    <Box>
      <HStack justifyContent="space-between" mb={6} flexWrap="wrap" gap={4}>
        <Box>
          <Text fontSize="lg" fontWeight="600" color="text.primary">
            {t('delegation.title')}
          </Text>
          <Text fontSize="sm" color="text.secondary">
            {t('delegation.subtitle')}
          </Text>
        </Box>
        <Button
          visual="primary"
          onClick={handleOpen}
          icon={<HiPlus />}
          disabled={students.length === 0}
          data-e2e="add-delegation-btn"
        >
          {t('delegation.addBtn')}
        </Button>
      </HStack>

      {isLoading ? (
        <Center py={12}>
          <Spinner size="lg" />
        </Center>
      ) : delegations.length > 0 ? (
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
          {delegations.map((d) => (
            <Box
              key={d.public_id}
              bg="surface.primary"
              border="1px solid"
              borderColor="border.secondary"
              borderRadius="lg"
              p={5}
              position="relative"
              data-e2e={`delegation-card-${d.public_id}`}
            >
              <HStack justifyContent="space-between" mb={3} alignItems="flex-start">
                <VStack align="stretch" gap={1}>
                  <Text fontSize="md" fontWeight="600" color="text.primary">
                    {d.delegate_name}
                  </Text>
                  <Text fontSize="xs" fontWeight="500" color="text.secondary">
                    {t('delegation.relationshipFor', { relationship: d.relationship, student: d.student_name })}
                  </Text>
                </VStack>
                <StatusBadge status={d.is_active ? 'active' : 'inactive'} />
              </HStack>

              <VStack align="stretch" gap={2} my={3} fontSize="sm" color="text.primary">
                {d.delegate_phone && (
                  <Text>
                    <strong>{t('delegation.phoneLabelShort', 'Phone')}:</strong> {d.delegate_phone}
                  </Text>
                )}
                {d.delegate_email && (
                  <Text>
                    <strong>{t('delegation.emailLabelShort', 'Email')}:</strong> {d.delegate_email}
                  </Text>
                )}
                <HStack gap={1} fontSize="xs" color="text.secondary">
                  <Icon as={HiCalendarDays} />
                  <Text>
                    {formatDateTime(d.valid_from)} - {formatDateTime(d.valid_until)}
                  </Text>
                </HStack>
              </VStack>

              {d.notes && (
                <Box bg="surface.secondary" p={3} borderRadius="md" mb={4}>
                  <Text fontSize="xs" fontWeight="600" color="text.secondary" mb={1}>
                    {t('delegation.notes')}
                  </Text>
                  <Text fontSize="xs" color="text.primary">
                    {d.notes}
                  </Text>
                </Box>
              )}

              <HStack justifyContent="flex-end" mt={2}>
                <Button
                  visual="secondary"
                  size="sm"
                  icon={d.is_active ? <HiXCircle /> : <HiCheckCircle />}
                  onClick={() => handleToggleActive(d.public_id, d.is_active)}
                  data-e2e={`toggle-delegation-${d.public_id}`}
                >
                  {d.is_active ? t('delegation.deactivate') : t('delegation.reactivate')}
                </Button>
                <Button
                  visual="secondary"
                  size="sm"
                  icon={<HiTrash />}
                  onClick={() => handleDelete(d.public_id)}
                  data-e2e={`delete-delegation-${d.public_id}`}
                >
                  {t('delegation.delete')}
                </Button>
              </HStack>
            </Box>
          ))}
        </SimpleGrid>
      ) : (
        <Center py={12} border="1px dashed" borderColor="border.secondary" borderRadius="lg">
          <VStack gap={2}>
            <HiUser size={32} />
            <Text fontSize="md" fontWeight="600" color="text.secondary">
              {t('delegation.empty')}
            </Text>
            <Text fontSize="sm" color="text.secondary" textAlign="center" maxW="400px">
              {students.length === 0
                ? t('delegation.noStudentsNotice')
                : t('delegation.authNotice')}
            </Text>
          </VStack>
        </Center>
      )}

      <DialogComponent
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        headingText={t('delegation.dialogTitle')}
        e2eTitle="add-delegation-dialog"
      >
        <form onSubmit={handleSubmit}>
          <VStack gap={4} align="stretch" mt={4}>
            <SelectInput
              label={t('delegation.selectStudent')}
              name="student"
              option={studentOptions}
              value={selectedStudentId}
              onChange={setSelectedStudentId}
              e2eTitle="delegation-student-select"
              searchPlaceholder={t('searchPlaceholder')}
              required
            />

            <Field.Root>
              <Field.Label>{t('delegation.delegateName')}</Field.Label>
              <InputField
                name="delegate_name"
                placeholder={t('delegation.namePlaceholder')}
                value={delegateName}
                onChange={(e) => setDelegateName(e.target.value)}
                required
                data-e2e="delegation-name-input"
              />
            </Field.Root>

            <Field.Root>
              <Field.Label>{t('delegation.relationship')}</Field.Label>
              <InputField
                name="relationship"
                placeholder={t('delegation.relationshipPlaceholder')}
                value={relationship}
                onChange={(e) => setRelationship(e.target.value)}
                required
                data-e2e="delegation-relationship-input"
              />
            </Field.Root>

            <Field.Root>
              <Field.Label>{t('delegation.phoneLabel')}</Field.Label>
              <InputField
                name="delegate_phone"
                type="tel"
                placeholder={t('delegation.phonePlaceholder')}
                value={delegatePhone}
                onChange={(e) => setDelegatePhone(e.target.value)}
                data-e2e="delegation-phone-input"
              />
            </Field.Root>

            <Field.Root>
              <Field.Label>{t('delegation.emailLabel')}</Field.Label>
              <InputField
                name="delegate_email"
                type="email"
                placeholder={t('delegation.emailPlaceholder')}
                value={delegateEmail}
                onChange={(e) => setDelegateEmail(e.target.value)}
                data-e2e="delegation-email-input"
              />
            </Field.Root>

            <HStack gap={3}>
              <Box flex="1">
                <Field.Root>
                  <Field.Label>{t('delegation.validFrom')}</Field.Label>
                  <InputField
                    type="datetime-local"
                    value={validFrom}
                    onChange={(e) => setValidFrom(e.target.value)}
                    required
                    data-e2e="delegation-from-input"
                  />
                </Field.Root>
              </Box>
              <Box flex="1">
                <Field.Root>
                  <Field.Label>{t('delegation.validUntil')}</Field.Label>
                  <InputField
                    type="datetime-local"
                    value={validUntil}
                    onChange={(e) => setValidUntil(e.target.value)}
                    required
                    data-e2e="delegation-until-input"
                  />
                </Field.Root>
              </Box>
            </HStack>

            <Box>
              <chakra.label fontSize="sm" color="text.secondary" mb={1} display="block">
                {t('delegation.notesLabel')}
              </chakra.label>
              <TextareaField
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('delegation.notesPlaceholder')}
                data-e2e="delegation-notes-input"
              />
            </Box>

            <HStack justifyContent="flex-end" gap={3} mt={4}>
              <Button visual="secondary" onClick={() => setIsOpen(false)} type="button">
                {t('delegation.cancel')}
              </Button>
              <Button visual="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('delegation.creating') : t('delegation.delegate')}
              </Button>
            </HStack>
          </VStack>
        </form>
      </DialogComponent>
    </Box>
  );
}
